// A live run's progress on its page (TOM-120, ADR 0248 §6, §7): results
// graded of planned per model, spend against the budget, errors so far, the
// daily cap as a launch sees it, the shards by state and the builds they ran
// on, and Cancel with a confirmation. The run page shows it while the run is
// queued, running or cancelling, and it stops polling once the run has ended.
// EvalProgressView draws one reading; EvalRunProgress polls and cancels.

import { useCallback, useEffect, useRef, useState } from 'react';

import { adminApiError } from '../adminApi';
import { formatDate } from '../format';
import { agentLabel } from './agents';
import { EvalBadge, StatusPill } from './EvalBits';
import { formatUsd, modelShortName } from './format';
import { evalLaunchApi } from './launchApi';
import {
  EVAL_PROGRESS_POLL_MS,
  MIXED_CODE_LABEL,
  buildText,
  canCancel,
  cancelErrorText,
  cancelOutcomeText,
  dailyText,
  isLiveRun,
  liveStateText,
  percentDone,
  progressTotals,
  shardText,
  spendText,
  type EvalRunProgress,
} from './progress';
import '../../styles/admin-evals-launch.css';

/** A labelled bar: a tinted fill inside a 1px border, with its figure beside it. */
export function EvalMeter({
  value,
  max,
  label,
  tone = 'neutral',
  testId,
}: {
  value: number;
  max: number;
  label: string;
  tone?: 'neutral' | 'warn';
  testId?: string;
}) {
  const pct = percentDone(value, max);
  return (
    <span
      className={`eval-meter eval-meter--${tone}`}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      data-testid={testId}
    >
      <span className="eval-meter-fill" style={{ width: `${pct}%` }} />
    </span>
  );
}

export interface EvalCancelControls {
  armed: boolean;
  busy: boolean;
  message: { ok: boolean; text: string } | null;
  onArm: () => void;
  onDisarm: () => void;
  onConfirm: () => void;
}

/** One reading of a live run's progress. */
export function EvalProgressView({
  progress: p,
  cancel,
  readAt,
}: {
  progress: EvalRunProgress;
  cancel: EvalCancelControls | null;
  readAt?: string | null;
}) {
  const totals = progressTotals(p);
  const state = liveStateText(p);
  const overBudget = p.budgetUsd !== null && p.spend.usd >= p.budgetUsd;
  return (
    <section className="eval-progress" aria-label="Run progress" data-testid="eval-progress">
      <header className="eval-progress-header">
        <h2>
          Progress <StatusPill status={p.status} />
        </h2>
        {readAt !== undefined && readAt !== null ? (
          <span className="admin-muted eval-sub">Read {formatDate(readAt)}</span>
        ) : null}
      </header>
      {state !== null ? (
        <p className="eval-progress-state" role="status" data-testid="eval-progress-state">
          {state}
        </p>
      ) : null}

      <div className="eval-progress-overall">
        <span className="eval-progress-figure" data-testid="eval-progress-total">
          {totals.complete} of {totals.planned} results graded
          <span className="admin-muted"> · {percentDone(totals.complete, totals.planned)}%</span>
        </span>
        <EvalMeter
          value={totals.complete}
          max={totals.planned}
          label="Results graded of planned"
          testId="eval-progress-meter"
        />
        <span className="admin-muted eval-sub">
          {totals.written} collected
          {totals.errored > 0 ? (
            <span className="eval-error-text"> · {totals.errored} errored</span>
          ) : (
            ' · no errors'
          )}
        </span>
      </div>

      {p.models.length > 0 ? (
        <ul className="eval-progress-models" aria-label="Each model's progress">
          {p.models.map((m) => (
            <li
              key={`${m.role}|${m.model}`}
              data-testid={`eval-progress-model-${m.role}-${m.model}`}
            >
              <span className="eval-progress-model-name" title={m.model}>
                <code>{modelShortName(m.model)}</code>
                {m.role !== 'chat' ? (
                  <span className="admin-muted"> {agentLabel(m.role)}</span>
                ) : null}
              </span>
              <span className="eval-progress-model-figure">
                {m.complete}/{m.planned}
              </span>
              <EvalMeter
                value={m.complete}
                max={m.planned}
                label={`${modelShortName(m.model)} results graded`}
              />
              <span className="admin-muted eval-sub eval-progress-model-sub">
                <span className={m.errored > 0 ? 'eval-error-text' : undefined}>
                  {m.errored} {m.errored === 1 ? 'error' : 'errors'}
                </span>{' '}
                · {formatUsd(m.costUsd)} + {formatUsd(m.judgeCostUsd)} judge
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <dl className="eval-kv eval-progress-kv">
        <dt>Spend</dt>
        <dd data-testid="eval-progress-spend">
          <span className={overBudget ? 'eval-error-text' : undefined}>{spendText(p)}</span>
          {p.budgetUsd !== null ? (
            <EvalMeter
              value={p.spend.usd}
              max={p.budgetUsd}
              label="Spend of the budget"
              tone={overBudget ? 'warn' : 'neutral'}
            />
          ) : null}
          <span className="admin-muted eval-sub eval-block">
            {formatUsd(p.spend.modelUsd)} models · {formatUsd(p.spend.judgeUsd)} judges ·{' '}
            {p.spend.calls} {p.spend.calls === 1 ? 'call' : 'calls'}
            {p.spend.errors > 0 ? ` (${p.spend.errors} failed)` : ''}
            {p.spend.unpricedCalls > 0 ? ` · ${p.spend.unpricedCalls} unpriced` : ''}
          </span>
        </dd>
        <dt>Daily cap</dt>
        <dd data-testid="eval-progress-daily">{dailyText(p.daily)}</dd>
        <dt>Shards</dt>
        <dd data-testid="eval-progress-shards">{shardText(p.shards)}</dd>
        <dt>Code</dt>
        <dd data-testid="eval-progress-code">
          {p.mixedCode ? (
            <EvalBadge
              tone="warn"
              title="Its shards ran on different code (a deploy landed mid-run)"
              testId="eval-mixed-code"
            >
              {MIXED_CODE_LABEL}
            </EvalBadge>
          ) : null}
          {p.code.length === 0 ? (
            <span className="admin-muted">No shard has started yet.</span>
          ) : (
            <ul className="eval-list eval-builds">
              {p.code.map((c, i) => (
                <li key={i}>
                  <code>{buildText(c)}</code>
                </li>
              ))}
            </ul>
          )}
        </dd>
        {p.error !== null ? (
          <>
            <dt>Error</dt>
            <dd className="eval-error-text">{p.error}</dd>
          </>
        ) : null}
      </dl>

      {cancel !== null && canCancel(p) ? (
        <div className="eval-progress-actions">
          {cancel.armed ? (
            <div className="admin-confirm" data-testid="eval-cancel-confirm">
              <span className="admin-confirm-prompt">
                Cancel this run? The case in flight finishes and is graded, the shards still queued
                are skipped, and the run ends cancelled with what it finished.
              </span>
              <button
                type="button"
                className="admin-button-danger"
                disabled={cancel.busy}
                onClick={cancel.onConfirm}
                data-testid="eval-cancel-confirm-button"
              >
                {cancel.busy ? 'Cancelling…' : 'Cancel run'}
              </button>
              <button type="button" className="admin-link-button" onClick={cancel.onDisarm}>
                Keep running
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="admin-button-danger"
              onClick={cancel.onArm}
              data-testid="eval-cancel"
            >
              Cancel run…
            </button>
          )}
        </div>
      ) : p.launchedBy === 'cli' && isLiveRun(p.status) ? (
        <p className="admin-muted eval-sub">
          The CLI launched this run, so it is stopped where it runs, not here.
        </p>
      ) : null}
      {cancel?.message !== null && cancel?.message !== undefined ? (
        <p
          className={cancel.message.ok ? 'eval-ok-text' : 'eval-error-text'}
          role="status"
          data-testid="eval-cancel-result"
        >
          {cancel.message.text}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Polls a live run's progress until it ends, then tells the page, which
 * reads the run again. Offers Cancel for a run the console or a schedule
 * launched.
 */
export function EvalRunProgress({ runId, onEnded }: { runId: string; onEnded: () => void }) {
  const [progress, setProgress] = useState<EvalRunProgress | null>(null);
  const [readAt, setReadAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [poke, setPoke] = useState(0);
  const ended = useRef(false);
  const onEndedRef = useRef(onEnded);
  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);

  useEffect(() => {
    let stopped = false;
    let timer: number | undefined;
    ended.current = false;
    const read = () => {
      evalLaunchApi
        .progress(runId)
        .then((p) => {
          if (stopped) return;
          setProgress(p);
          setReadAt(new Date().toISOString());
          setError(null);
          if (isLiveRun(p.status)) {
            timer = window.setTimeout(read, EVAL_PROGRESS_POLL_MS);
          } else if (!ended.current) {
            // The run has ended: stop polling, and let the page read it again.
            ended.current = true;
            onEndedRef.current();
          }
        })
        .catch((err) => {
          if (stopped) return;
          setError(
            (err as { status?: number }).status === 404
              ? 'No such run.'
              : 'Could not read the run’s progress. Trying again.'
          );
          timer = window.setTimeout(read, EVAL_PROGRESS_POLL_MS);
        });
    };
    read();
    return () => {
      stopped = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [runId, poke]);

  const confirm = useCallback(() => {
    setBusy(true);
    setMessage(null);
    evalLaunchApi
      .cancel(runId)
      .then((res) => {
        setArmed(false);
        setMessage({ ok: true, text: cancelOutcomeText(res.outcome) });
        setPoke((n) => n + 1);
      })
      .catch((err) => {
        setMessage({ ok: false, text: cancelErrorText(adminApiError(err)) });
      })
      .finally(() => setBusy(false));
  }, [runId]);

  if (progress === null) {
    return (
      <section className="eval-progress" aria-label="Run progress" data-testid="eval-progress">
        {error !== null ? (
          <p className="eval-error-text" role="alert">
            {error}
          </p>
        ) : (
          <p className="admin-loading-row">Reading progress…</p>
        )}
      </section>
    );
  }
  return (
    <>
      <EvalProgressView
        progress={progress}
        readAt={readAt}
        cancel={{
          armed,
          busy,
          message,
          onArm: () => {
            setMessage(null);
            setArmed(true);
          },
          onDisarm: () => setArmed(false),
          onConfirm: confirm,
        }}
      />
      {error !== null ? (
        <p className="eval-error-text eval-sub" role="alert">
          {error}
        </p>
      ) : null}
    </>
  );
}
